/**
 * AdminPageHeader — unified header for every admin list/detail page.
 *
 * Breadcrumb (gold tracking-widest) > serif italic page title with optional
 * hairline count badge > primary GoldFoilButton + optional secondary
 * HairlineButton on the right. Below it a thin gold rule.
 *
 * Mobile: stacks the actions below the title.
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CircleHelp } from "lucide-react";
import { helpSectionForPath } from "@/lib/helpContent";
import {
  GoldRule, GoldFoilButton, HairlineButton,
} from "@/components/editorial";

export function HairlineBadge({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full border border-[color:var(--gold-foil)]/55 text-[color:var(--gold-foil)] ed-eyebrow ${className}`}
    >
      {children}
    </span>
  );
}

export default function AdminPageHeader({
  breadcrumb,            // e.g. ["WORKFLOW", "BOOKINGS"]
  title,                 // string — the page name
  count,                 // optional number for the badge
  primary,               // { label, href?, onClick?, testid? }
  secondary,             // { label, href?, onClick?, testid? }
  rule = true,           // render the bottom gold rule
  rightSlot,             // optional custom React node on the right side
  className = "",
}) {
  const crumb = Array.isArray(breadcrumb) ? breadcrumb.join(" / ") : (breadcrumb || "");
  const { pathname } = useLocation();
  const help = pathname.startsWith("/admin/help") ? null : helpSectionForPath(pathname);
  return (
    <header className={`pt-2 pb-6 ${className}`} data-testid="admin-page-header">
      <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between md:gap-8">
        <div>
          {crumb || help ? (
            <p className="ed-eyebrow text-[color:var(--gold-base)] mb-3 flex items-center gap-3">
              {crumb}
              {help && (
                <Link
                  to={`/admin/help#${help.id}`}
                  className="inline-flex items-center gap-1 normal-case tracking-normal text-[11px] text-[color:var(--cream)]/50 hover:text-[color:var(--cream)] transition-colors"
                  data-testid="page-help-link"
                >
                  <CircleHelp className="w-3.5 h-3.5" /> How this works
                </Link>
              )}
            </p>
          ) : null}
          <div className="flex items-baseline gap-4">
            <h1 className="font-serif italic text-4xl md:text-5xl leading-tight tracking-tight text-[color:var(--cream)]">
              {title}
            </h1>
            {typeof count === "number" ? (
              <HairlineBadge>{count}</HairlineBadge>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {rightSlot}
          {secondary ? (
            secondary.href ? (
              <HairlineButton href={secondary.href} tone="dark" data-testid={secondary.testid}>
                {secondary.label}
              </HairlineButton>
            ) : (
              <HairlineButton onClick={secondary.onClick} tone="dark" data-testid={secondary.testid}>
                {secondary.label}
              </HairlineButton>
            )
          ) : null}
          {primary ? (
            primary.href ? (
              <GoldFoilButton href={primary.href} tone="dark" data-testid={primary.testid}>
                {primary.label}
              </GoldFoilButton>
            ) : (
              <GoldFoilButton onClick={primary.onClick} tone="dark" data-testid={primary.testid}>
                {primary.label}
              </GoldFoilButton>
            )
          ) : null}
        </div>
      </div>
      {rule ? <GoldRule width="100%" tone="soft" className="mt-6" /> : null}
    </header>
  );
}
